import React, { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Dialog, DialogContent, DialogTitle } from '../../material-ui/Dialog';
import { List, ListItem, ListItemIcon, ListItemText } from '../../material-ui/List';
import { TextField } from '../../material-ui/TextField';
import { Button } from '../../material-ui/Button';
import { Checkbox } from '../../material-ui/Checkbox';
import { Typography } from '../../material-ui/Typography';
import { useStyles } from '../useStyles';

/*
 * Состав группы: показывает текущих участников и позволяет добавить новых
 * (множественный выбор из адресатов, исключая уже состоящих). Изолированный
 * компонент: данные и колбэки приходят сверху, Redux не знает.
 */
export const GroupMembersDialog = memo(({ open, chat, members, recipients, onAdd, onClose }) => {
    const classes = useStyles();
    const { t: translate } = useTranslation();
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState([]);

    useEffect(() => {
        if (!open) {
            setSearch('');
            setSelected([]);
        }
    }, [open]);

    const memberIds = useMemo(() => new Set((members || []).map((member) => member.id)), [members]);

    const candidates = useMemo(() => {
        const query = search.trim().toLowerCase();

        return (recipients || [])
            .filter((recipient) => !memberIds.has(recipient.id))
            .filter((recipient) => !query || (recipient.name || '').toLowerCase().includes(query));
    }, [recipients, memberIds, search]);

    const toggle = (id) => setSelected((prev) => (prev.indexOf(id) === -1 ? [...prev, id] : prev.filter((value) => value !== id)));

    const submit = () => {
        if (selected.length > 0) {
            onAdd(selected);
        }
    };

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>{(chat && chat.title) || translate('messenger.members')}</DialogTitle>
            <DialogContent className={classes.composeContent}>
                <Typography variant="subtitle2" className={classes.composeSubtitle}>
                    {`${translate('messenger.membersTitle')} (${(members || []).length})`}
                </Typography>
                <List className={classes.memberList}>
                    {(members || []).map((member) => (
                        <ListItem key={member.id}>
                            <ListItemText primary={member.name} secondary={member.isOwner ? translate('messenger.owner') : undefined} />
                        </ListItem>
                    ))}
                </List>

                <Typography variant="subtitle2" className={classes.composeSubtitle}>
                    {translate('messenger.addMembers')}
                </Typography>
                <div className={classes.composeGroupRow}>
                    <TextField
                        fullWidth={true}
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder={translate('messenger.membersSearch')}
                    />
                    <Button
                        color="primary"
                        variant="contained"
                        disabled={selected.length === 0}
                        onClick={submit}
                        className={classes.composeGroupButton}
                    >
                        {selected.length > 0 ? `${translate('messenger.add')} (${selected.length})` : translate('messenger.add')}
                    </Button>
                </div>
                <List className={classes.recipientList}>
                    {candidates.length > 0 ? (
                        candidates.map((recipient) => (
                            <ListItem button={true} key={recipient.id} onClick={() => toggle(recipient.id)}>
                                <ListItemIcon className={classes.composeCheckCell}>
                                    <Checkbox
                                        edge="start"
                                        color="primary"
                                        tabIndex={-1}
                                        disableRipple={true}
                                        checked={selected.indexOf(recipient.id) !== -1}
                                    />
                                </ListItemIcon>
                                <ListItemText primary={recipient.name} />
                            </ListItem>
                        ))
                    ) : (
                        <ListItem>
                            <ListItemText primary={translate('messenger.noRecipients')} />
                        </ListItem>
                    )}
                </List>
            </DialogContent>
        </Dialog>
    );
});

GroupMembersDialog.displayName = 'GroupMembersDialog';
